import groupBy from "lodash/groupBy";
import type { IssueCount } from "../../data/useIssueCounts";
import { formatInt } from "../../lib/utils";
import { getThemeColour } from "./themeColours";

import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { TitleComponent, TooltipComponent } from "echarts/components";
import { SunburstChart as SunRiseChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

//register echarts components
echarts.use([TitleComponent, SunRiseChart, CanvasRenderer, TooltipComponent]);

interface IssueSunburstProps {
  issues: IssueCount[];
}

export default function IssueSunburst({
  issues,
}: IssueSunburstProps): JSX.Element {
  const grouped = groupBy(issues, "theme");

  const themes = Object.keys(grouped)
    .map((theme) => {
      const themeIssues = grouped[theme].sort((a, b) => {
        return b.count - a.count !== 0
          ? b.count - a.count
          : a.issue.localeCompare(b.issue);
      });
      return {
        theme,
        count: themeIssues.reduce((sum, issue) => sum + issue.count, 0),
        issues: themeIssues,
      };
    })
    .sort((a, b) => b.count - a.count);

  const chartData = themes.map((theme) => ({
    name: `${theme.theme}`,
    value: theme.count,
    itemStyle: {
      color: getThemeColour(theme.theme),
    },
    children: theme.issues.map((issue) => ({
      name: `${issue.issue}`,
      value: issue.count,
      itemStyle: {
        color: getThemeColour(theme.theme),
      },
    })),
  }));

  const option = {
    tooltip: {
      trigger: "item",
      formatter: (params: any) => {
        //params.name = {b}
        //params.value = {c}
        const formattedValue = formatInt(params.value);
        return `${params.name}\n${formattedValue}`;
      },
    },
    series: {
      type: "sunburst",
      data: chartData,
      radius: [0, "100%"],
      center: ["50%", "50%"],
      sort: undefined,
      emphasis: {
        focus: "ancestor",
      },
      levels: [
        {
          r0: "5%",
          r: "20%",
        },
        {
          r0: "20%",
          r: "60%",
          label: {
            align: "center",
            minAngle: 8,
            formatter: (params: any) => {
              const formattedValue = formatInt(params.value);
              return `${params.name}\n${formattedValue}`;
            },
          },
        },
        {
          r0: "60%",
          r: "90%",
          label: {
            padding: 3,
            silent: false,
            minAngle: 18,
            rotate: "radial",
            overflow: "break",
            width: 70,
            formatter: (params: any) => {
              const formattedValue = formatInt(params.value);
              return `${params.name}\n${formattedValue}`;
            },
          },
          itemStyle: {
            borderWidth: 3,
          },
        },
      ],
    },
  };

  return (
    <div style={{ width: "100%", padding: 0, margin: 0 }}>
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        notMerge={true}
        lazyUpdate={true}
        opts={{ renderer: "canvas" }}
        style={{
          minHeight: 500,
          width: "100%",
          padding: 0,
          marginTop: "-25px",
        }}
      />
    </div>
  );
}
